import { ImportJobStatusName } from 'common/constants/admin';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';

import { AclEntriesStatusTooltipProps } from './TypeDefs';

export type ApplicationSuite = {
    id: string,
    name: string,
    tenantsCount?: number
}

export type Tenant = {
    id: string,
    applicationSuiteId: string,
    name: string
}

export type AppSuiteTenant = {
    id: string;
    applicationSuiteId: string;
    name: string;
    isLicenseRequired: boolean;
}

export type ApiKey = {
    applicationSuiteId: string;
    name: string;
    value: string;
    isAdmin: boolean;
}

export type AclResource = {
    tenantId: string;
    id: string;
    name: string;
    displayName: string;
    conditions?: string[];
}

export type AclAccess = {
    tenantId: string;
    id: string;
    name: string;
}

export type AclCondition = {
    name: string;
    key: string;
}

export type AclEntryCondition = {
    conditionKey: string;
    parameters: Record<string, string>;
}

export type Acl = {
    // ids
    tenantId: string;
    accessId: string;
    resourceId: string;
    userId?: string;
    usergroupId?: string;
    licenseId?: string;
    // names
    tenantName?: string;
    accessName: string;
    resourceName: string;
    resourceDisplayName?: string;
    usergroupName?: string;
    // dates
    startDate?: string;
    endDate?: string;
    // conditions
    conditions?: AclEntryCondition[];
    conditionsToDisplay?: string;
}

export type AclFormatted = Acl & {
    //combined id for data grid
    id?: string;
    //dynamically calculated field
    principalName?: string;
    //resource display name in data grid
    resourceTableName?: string;
    //access display name in data grid
    accessTableName?: string;
}

export type FormattedAclWithLicenseInfo = AclFormatted & {
    licenseBillingRecord?: string;
    status?: EntitiesStatus;
    statusTooltip?: AclEntriesStatusTooltipProps;
    currentPage?: AclEntriesPage;
};

export type AclsWithProfiles = Acl & {
    //combined id for data grid
    id?: string;
    principalName?: string;
    companyKey?: string;
    email?: string;
    //resource display name in data grid
    resourceTableName?: string;
    //access display name in data grid
    accessTableName?: string;
};

export type PaginatedAclsWithProfilesFormatted = {
    data: AclsWithProfiles[];
    skipped: number;
    taken: number;
    count: number;
}

export enum EntitiesStatus {
    ACTIVE = 'Active',
    INACTIVE = 'Inactive',
    NOACCESS = 'NoAccess'
}

export enum PrincipalType {
    User = 'User',
    UserGroup = 'User Group'
}

export type License = {
    id: string;
    tenantId: string;
    companyKey: string;
    note: string;
    startDate: string;
    endDate?: string;
    billingSystemName?: string;
    billingRecord?: string;
    licenseAutoRenewal?: boolean;
}

export type LicenseFormatted = License & {
    name: string;
    status: EntitiesStatus;
}

export type AuthorizedIssuer = {
    tenantId: string;
    identityproviderId: string;
    identityProviderName: string;
    audience: string;
    isGlobal: boolean;
}

export type IdentityProvider = {
    id: string;
    name: string;
    issuer: string;
}

export type UserGroup = {
    id: string;
    name: string;
    description: string;
    tenantId: string;
}

export type UserGroupWithCreationFlag = UserGroup & { shouldDisplayCreateNewUserGroupFields?: boolean };

export type UserGroupDropdownOption = UserGroup | UserGroupWithCreationFlag;

export type ClaimDataType = {
    id: string;
    name: string;
    description: string;
}

export type Claim = {
    id: string;
    name: string;
    tenantId: string;
    claimDataTypeId: string;
    claimDataTypeName: string;
}

export type UserGroupRoleMembership = {
    roleId: string;
    roleName: string;
    userGroupId: string;
    userGroupName: string;
    userGroupDescription: string;
}

export type BillingInformation = {
    user_status: string;
    encrypted_user_id?: string;
    account_id: string;
    user_id?: string;
    is_email_verified?: boolean;
    namespace?: string;
    is_administrator?: boolean;
    last_name?: string;
    first_name?: string;
    email?: string;
    subject?: string;
    email_login_status: string;
}

export type BillingSystems = {
    id: string;
    name: string;
}

export type Role = {
    id: string;
    name: string;
    tenantId: string;
}

export type GlobalRole = {
    name: string;
    id: string;
    description: string;
}

export type IdentityOemMapping = {
    authority: string;
    subject: string;
    firstName: string;
    lastName: string;
    email: string;
    oemId: string;
    oemIdPId: string;
    oemIdPName: string;
    billingInformation?: BillingInformation[];
}

export type UserProfile = {
    firstName: string;
    lastName: string;
    companyKey: string;
    email: string;
    principalName: string;
}

export type UserIdentity = {
    subject: string;
    issuer: string;
}

export type UserProfileOemMapping = {
    authority: string;
    firstName: string;
    lastName: string;
    companyKey?: string;
    oemIdPName: string;
    oemId: string;
    subject: string;
    email: string;
    oemIdPId: string;
    activeIdentityStatus: boolean;
}

export type UserTenantMembership = {
    userId: string;
    tenantId: string;
    isActive?: boolean;
    oasysId: string;
    notes: string;
    globalRoles: GlobalRole[];
    profile: IInitialUser;
    startDate: string;
    endDate?: string;
};

export type UserClaimMembership = {
    userId: string;
    claimName: string;
    claimId: string;
    claimDataTypeName: string;
    claimDataTypeId: string;
    id: string;
    value: string;
}

export type UserDetails = {
    userId: string,
    firstName: string,
    lastName: string,
    userEmail: string,
    companyKey?: string,
    oasysId?: string,
    identities?: {
        subject: string;
        issuer: string;
        profile?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            companyKey?: string;
        }
    }[]
};

export type UserForeignKey = {
    userId: string,
    foreignSystemId: string,
    foreignSystemName: string,
    foreignKey: string
};

export type UserProfileApps = {
    id: string;
    name: string;
};

export type UserProfileIdentityProviders = {
    userProfileInfo: {
        firstName: string;
        lastName: string;
        userEmail: string;
        companyKey: string;
    };
    id: string;
    name: string;
    issuer: string;
    subject: string;
}

export type ForeignSystem = {
    id: string,
    name: string,
};

export type ImportType = {
    id: string,
    name: string,
};

export type ImportJobStatus = {
    id?: string,
    name?: string,
    s3KeyForImportFile?: string,
    importTypeName?: string,
    tenantId?: string,
    s3PreSignedKickbackUrl?: string,
    importStatusName: ImportJobStatusName,
    jobCreationDateTime?: string,
    latestStatusDateTime?: string
};

export interface IInitialUser {
    index?: number;
    companyKey: string;
    firstName: string;
    id: string;
    lastName: string;
    profile : {
        firstName: string;
        lastName: string;
        companyKey: string;
        email: string
    }
    userEmail: string
    userId: string
}

export interface ILicensesState {
    licenses: License[],
    licensesByTenantId: Record<string, License[]>,
    loaded: boolean,
    filters: {
        keywords: string[];
        dropdowns: {
            status: {
                keys: string[];
                asKeyword: boolean
            };
        }
    },
}

export type ProductPackage = {
    productPackageId: string;
    tenantId: string;
    packageName: string;
    packageNote: string;
}

export type PackageAssignment = {
        tenantId: string;
        productPackageId: string;
        userId: string;
        userGroupId: string;
        licenseId: string;
        startDate: string;
        endDate: string;
        isActive: boolean;
        packageName: string;
        billingRecord: string;
        principalName: string;
        conditions: AclEntryCondition[];
}

export type InheritedProductPackageAssignment = {
    tenantId: string;
    productPackageId: string;
    userId: string;
    packageName: string;
    userGroupId?: string;
    userGroupName?: string;
    licenseId?: string;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    conditions?: AclEntryCondition[];
}